import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Briefcase, Lock, MapPin, Star, Loader2, ShieldCheck, ArrowRight, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import ScarcityWidget from './ScarcityWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Role-specific framing so the same panel speaks to each persona naturally.
// All roles route to the SAME /api/pro-leads endpoint — only copy changes.
const ROLE_COPY = {
  buyer: {
    eyebrow: 'Local Pros · Verified by Google',
    heading: 'Trusted estate agents, solicitors & brokers near you',
    sub: 'Independent professionals — no Shopperden agency markup. Unlock direct contact for {fee}.',
  },
  seller: {
    eyebrow: 'Find Your Listing Partner',
    heading: 'Local agents & solicitors in {city}',
    sub: 'Pick your own representation. We surface Google-rated pros — you pick the one that fits. Unlock direct contact for {fee}.',
  },
  tenant: {
    eyebrow: 'Letting Agents & Conveyancers',
    heading: 'Letting agents who cover {city}',
    sub: 'Verified by Google reviews. Unlock the direct line for {fee} — no agency referral fees on top.',
  },
  agent: {
    eyebrow: 'Partner Network',
    heading: 'Solicitors & brokers to refer your buyers to',
    sub: 'Build your own panel of vetted pros. Unlock contact details for {fee} — once unlocked, theirs forever.',
  },
  professional: {
    eyebrow: 'Cross-Refer Network',
    heading: 'Estate agents & brokers in {city}',
    sub: 'Build reciprocal referral pipelines. Unlock direct contact for {fee} — share clients, share commissions.',
  },
  nri: {
    eyebrow: 'On-Ground Pros for NRIs',
    heading: 'Solicitors, agents & brokers on the ground in {city}',
    sub: 'Verified locally so you don\'t fly blind. Unlock the contact bundle for {fee} — emailed instantly with a warm intro on your behalf.',
  },
};


const _formatFee = (fee) => {
  if (!fee?.amount) return '£15';
  const ccy = (fee.currency || '').toLowerCase();
  const sym = ccy === 'inr' ? '₹' : ccy === 'cad' ? 'C$' : '£';
  // INR shown without decimals; ₹1,535 reads cleaner than ₹1,535.00.
  const amount = ccy === 'inr'
    ? Number(fee.amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })
    : Number(fee.amount).toFixed(2);
  return `${sym}${amount}`;
};


const LeadCard = ({ lead, onUnlock, loading }) => {
  const rating = lead.rating ? Number(lead.rating).toFixed(1) : null;
  return (
    <div
      data-testid={`pro-lead-card-${lead.place_id}`}
      className="border border-slate-200 rounded-md p-4 bg-white hover:shadow-md transition-shadow flex flex-col gap-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] tracking-[0.2em] text-[#E07A3A] uppercase font-semibold mb-1">
            {(lead.category || 'Professional').replace(/\b\w/g, (c) => c.toUpperCase())}
          </p>
          <h4 className="font-semibold text-[#0F172A] truncate">{lead.name || '—'}</h4>
          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{lead.city}{lead.country ? `, ${lead.country}` : ''}</span>
          </div>
        </div>
        {rating && (
          <div className="flex flex-col items-end flex-shrink-0">
            <div className="flex items-center gap-1 text-amber-600">
              <Star className="h-3.5 w-3.5 fill-current" />
              <span className="text-sm font-bold">{rating}</span>
            </div>
            <span className="text-[10px] text-slate-400">{lead.review_count || 0} reviews</span>
          </div>
        )}
      </div>

      {lead.unlocked ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800 space-y-1" data-testid={`pro-lead-unlocked-${lead.place_id}`}>
          <div className="flex items-center gap-2 text-emerald-700 font-semibold">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span>Unlocked — direct contact</span>
          </div>
          {lead.phone && <div><b>Phone:</b> {lead.phone}</div>}
          {lead.email && <div><b>Email:</b> {lead.email}</div>}
          {lead.website && (
            <div className="truncate">
              <b>Web:</b> <a href={lead.website} target="_blank" rel="noopener noreferrer" className="underline">{lead.website}</a>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="text-xs text-slate-500 space-y-0.5 bg-slate-50 rounded p-2">
            <div className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              <span className="font-mono">{lead.phone_masked || '••• ••• •••'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              <span>Website + email hidden</span>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => onUnlock(lead)}
            disabled={loading}
            className="w-full bg-[#E07A3A] hover:bg-[#C4652E] text-white text-xs tracking-widest font-semibold"
            data-testid={`pro-lead-unlock-btn-${lead.place_id}`}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Lock className="h-3.5 w-3.5 mr-1.5" />}
            Unlock contact
            <ArrowRight className="h-3 w-3 ml-1.5" />
          </Button>
        </>
      )}
    </div>
  );
};


/**
 * LocalProsPanel — single source of truth across every role dashboard.
 *
 * Props:
 *   role     — 'buyer' | 'seller' | 'tenant' | 'agent' | 'professional' | 'nri'
 *   city     — optional preferred city
 *   country  — 'UK' | 'IN' | 'CA'
 *   dataTestId — root test-id (defaults to "local-pros-panel-{role}")
 *
 * Behaviour:
 *  - Fetches /api/pro-leads with the given filters.
 *  - Renders 6 cards by default (under MAX_LIST_SIZE).
 *  - Contact rows are redacted unless this user has paid the £15/₹499/C$25
 *    Connection Fee — which is the SAME toll as the property Vault Unlock.
 *  - "Unlock contact" → POST /api/payments/pro-unlock/checkout → Stripe.
 */
const LocalProsPanel = ({
  role = 'buyer',
  city = '',
  country = 'UK',
  dataTestId,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);
  const copy = ROLE_COPY[role] || ROLE_COPY.buyer;
  const testId = dataTestId || `local-pros-panel-${role}`;

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (city) params.set('city', city);
      if (country) params.set('country', country);
      params.set('limit', '6');
      const res = await axios.get(`${API_URL}/api/pro-leads?${params.toString()}`);
      setData(res.data);
    } catch {
      setData({ items: [], count: 0 });
    } finally {
      setLoading(false);
    }
  }, [city, country]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleUnlock = async (lead) => {
    setUnlocking(lead.place_id);
    try {
      const res = await axios.post(`${API_URL}/api/payments/pro-unlock/checkout`, {
        place_id: lead.place_id,
        country: lead.country || country,
        origin_url: window.location.origin,
      });
      if (res.data?.already_unlocked) {
        toast.success('Already unlocked — refreshing');
        fetchLeads();
      } else if (res.data?.freemium) {
        toast.success('First unlock free — contact details emailed to you');
        fetchLeads();
      } else if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 401) {
        toast.error('Sign in first to unlock pro contacts');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Unlock checkout failed');
      }
    } finally {
      setUnlocking(null);
    }
  };

  const fee = data?.unlock_fee;
  const feeLabel = _formatFee(fee);
  const heading = copy.heading.replace('{city}', city || 'your area');
  const sub = copy.sub.replace('{fee}', feeLabel).replace('{city}', city || 'your area');

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 sm:p-6 mb-6" data-testid={testId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-[#E07A3A]/10 border border-[#E07A3A]/30 p-2 rounded flex-shrink-0">
          <Briefcase className="h-5 w-5 text-[#E07A3A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-semibold">
            {copy.eyebrow}
          </p>
          <h3 className="font-['Playfair_Display'] text-xl sm:text-2xl text-[#0F172A] mt-1 leading-tight">
            {heading}
          </h3>
          <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">{sub}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10" data-testid={`${testId}-loading`}>
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (data?.items || []).length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8" data-testid={`${testId}-empty`}>
          No local pros indexed yet for this area — our harvester picks up new cities every rotation.
        </p>
      ) : (
        <>
          {city && <ScarcityWidget city={city} country={country} dataTestId={`${testId}-scarcity`} />}

          {/* Freemium hint — first unlock free for verified users.
              Biggest funnel-widener on the platform. The actual entitlement
              is enforced server-side at /api/payments/pro-unlock/checkout. */}
          {data?.authenticated && (
            <div className="flex items-center gap-2 text-xs sm:text-[13px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3 mb-4" data-testid={`${testId}-freemium`}>
              <Gift className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              <span>Your <b>first contact unlock is free</b> once you verify your phone — no card needed.</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={`${testId}-grid`}>
            {(data?.items || []).map((lead) => (
              <LeadCard
                key={lead.place_id}
                lead={lead}
                onUnlock={handleUnlock}
                loading={unlocking === lead.place_id}
              />
            ))}
          </div>
        </>
      )}

      <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
        Connection Fee {feeLabel} — same fee as our property Vault Unlock. Unlocks
        verified phone, email & website. Independent service: Shopperden never
        takes a cut from the professional. <ShieldCheck className="inline h-3 w-3 -mt-0.5" /> Profit-Guard pricing.
      </p>
    </div>
  );
};

export default LocalProsPanel;
